import { loadAllAndAssert } from '../../authz/access.js';
import { logger } from '@adornis/base/logging.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import { getLLMRouter } from '../llm/llm-gate.js';
import { Observable } from 'rxjs';
import { imagesToBase64URLsWithSizeFallback } from '../../../db/utils/transform-file.js';
import {
  structureAgentDefaultTemperature,
  structureAgentModel,
  structureAgentRetryTemperature,
} from '../../../shared/models.js';
import { structureAgentSystemPrompt } from '../../prompts.js';
import { TaskType } from '../task-types/task-main.js';

interface StructureNode {
  taskNumber?: string;
  taskType?: TaskType;
  needsAnswer?: boolean;
  subtasks?: StructureNode[];
}

function isValidStructureNode(node: unknown): boolean {
  if (!node || typeof node !== 'object') return false;

  const typedNode = node as StructureNode;
  if (typeof typedNode.taskNumber !== 'string' || typedNode.taskNumber.trim().length === 0) {
    return false;
  }

  // taskType must be a string and one of the known TaskType values
  const allowedTaskTypes = new Set(Object.values(TaskType));
  if (typeof typedNode.taskType !== 'string' || !allowedTaskTypes.has(typedNode.taskType as TaskType)) {
    return false;
  }

  if (typeof typedNode.needsAnswer !== 'boolean') {
    return false;
  }

  if (!Array.isArray(typedNode.subtasks)) {
    return false;
  }

  return typedNode.subtasks.every(child => isValidStructureNode(child));
}

function isValidStructureResult(answer: string): boolean {
  try {
    const parsed = JSON.parse(answer);

    if (!Array.isArray(parsed?.tasks) || parsed.tasks.length === 0) {
      return false;
    }

    return parsed.tasks.every((task: unknown) => isValidStructureNode(task));
  } catch {
    return false;
  }
}

async function runStructureWithValidation(
  contentParts: any[],
  maxRetries = 1,
): Promise<{ answer: string; cost: number; temperature: number }> {
  let retryCount = 0;
  let lastCost = 0;
  let usedTemperature = 0;

  while (retryCount <= maxRetries) {
    const router = getLLMRouter();
    const temperature = retryCount > 0 ? structureAgentRetryTemperature : structureAgentDefaultTemperature;
    usedTemperature = temperature;

    logger.info({ attempt: retryCount + 1, temperature }, 'Structure Agent attempt with temperature');

    try {
      const response = await router.chat.completions.create({
        model: structureAgentModel,
        provider: 'openrouter',
        temperature,
        messages: [
          { role: 'system', content: structureAgentSystemPrompt },
          { role: 'user', content: contentParts },
        ],
        response_format: { type: 'json_object' },
      });

      logger.info({ usage: JSON.stringify(response.usage) }, 'LLM call cost for this turn');
      const rawContent = response.choices?.[0]?.message?.content;
      const finalAnswer = typeof rawContent === 'string' ? rawContent : JSON.stringify(rawContent ?? '');

      const usageCost = Number((response.usage as any)?.cost ?? 0);
      logger.info({ usageCost: JSON.stringify(usageCost), context: 'COST_TRACKING' }, 'Structure Agent cost');
      lastCost += usageCost;

      if (isValidStructureResult(finalAnswer)) {
        logger.info({ attempt: retryCount + 1, finalAnswer }, 'Structure Agent raw answer passed on attempt');
        return { answer: finalAnswer, cost: lastCost, temperature: usedTemperature };
      }

      logger.warn({ attempt: retryCount + 1 }, 'Structure validation failed on attempt');
    } catch (error) {
      logger.error(
        { err: error, attempt: retryCount + 1, context: 'StructureAgent' },
        'Network request or API token failure on attempt',
      );
    }

    retryCount++;
  }

  // If all retries failed, return empty valid JSON structure
  logger.warn(
    { totalAttempts: maxRetries + 1 },
    'Structure validation failed after all attempts. Returning fallback result.',
  );
  return {
    answer: JSON.stringify({ tasks: [] }),
    cost: lastCost,
    temperature: usedTemperature,
  };
}

export const structureAgent = registerSubscription({
  operationName: 'structureAgent',
  type: () => String, // Output as JSON string to avoid _class error
  params: [{ name: 'imageIds', type: () => [String] }],
  resolve: (imageIds: string[]) => () =>
    new Observable<string>(subscriber => {
      void (async () => {
        try {
          const imageIDs = imageIds || [];
          if (imageIDs.length === 0) {
            subscriber.error(new Error('No images provided in submission.'));
            return;
          }
          await loadAllAndAssert(ImageFile, imageIDs.filter(Boolean), 'read', { _id: 1 });
          const images: ImageFile[] = [];
          for (const imageId of imageIDs) {
            if (!imageId) continue;
            const image = await getByID(ImageFile, imageId)(ImageFile.allFields);
            if (image) {
              const imageFile =
                image instanceof ImageFile
                  ? image
                  : new ImageFile({
                      ...image,
                      meta: image.meta ?? {},
                      createdAt: image.createdAt ?? new Date(),
                      _id: image._id ?? '',
                      _class: image._class ?? ImageFile._class,
                    });
              images.push(imageFile);
            }
          }
          if (!images.length) {
            subscriber.error(new Error('No images found for submission.'));
            return;
          }
          const imageBase64urls = await imagesToBase64URLsWithSizeFallback(images, { logTag: '[Structure]' });
          const contentParts: any[] = [
            {
              type: 'text',
              text: `Extract the task structure from these exam pages (${imageBase64urls.length} Page(s)).`,
            },
            ...imageBase64urls.map(url => ({
              type: 'image_url',
              image_url: { url, detail: 'low' },
            })),
          ];

          const { answer: finalAnswer, cost: totalCost } = await runStructureWithValidation(
            contentParts,
            1, // max 1 retry
          );

          subscriber.next(
            JSON.stringify({
              answer: finalAnswer,
              cost: Number.isFinite(totalCost) ? totalCost : 0,
            }),
          );
        } catch (e) {
          logger.error({ err: e, context: 'structureAgent' }, 'Severe breakdown in execution pipeline');
          subscriber.error(
            JSON.stringify({
              error: e instanceof Error ? e.message : String(e),
              tasks: [],
              cost: 0,
            }),
          );
        } finally {
          subscriber.complete();
        }
      })();
    }),
});
